// src/services/themesService.js
import api from './api';

export const themesAPI = {
  // Theme operations
  getThemes: () => api.get('/Themes'),
  getTheme: (id) => api.get(`/Themes/${id}`),
  createTheme: (themeData) => api.post('/Themes', themeData),
  updateTheme: (id, themeData) => api.put(`/Themes/${id}`, themeData),
  deleteTheme: (id) => api.delete(`/Themes/${id}`),
  
  // Category operations
  getThemeCategories: (themeId) => api.get(`/Themes/${themeId}/categories`),
  createCategory: (themeId, categoryData) => api.post(`/Themes/${themeId}/categories`, categoryData),
  updateCategory: (themeId, categoryId, categoryData) => api.put(`/Themes/${themeId}/categories/${categoryId}`, categoryData),
  deleteCategory: (themeId, categoryId) => api.delete(`/Themes/${themeId}/categories/${categoryId}`),
  
  // Question operations
  getThemeQuestions: (themeId) => api.get(`/Themes/${themeId}/questions`),
  createQuestion: (themeId, questionData) => api.post(`/Themes/${themeId}/questions`, questionData),
  updateQuestion: (themeId, questionId, questionData) => api.put(`/Themes/${themeId}/questions/${questionId}`, questionData),
  deleteQuestion: (themeId, questionId) => api.delete(`/Themes/${themeId}/questions/${questionId}`),
  
  // Apply theme
  applyTheme: (applyData) => api.post('/Themes/apply', applyData),
};

export const themesService = {
  // Theme operations
  getThemes: async () => {
    try {
      console.log('🎨 Fetching themes from API...');
      const response = await themesAPI.getThemes();
      console.log('✅ Themes response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching themes:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      throw new Error(error.response?.data?.message || 'Failed to load themes');
    }
  },

  getTheme: async (themeId) => {
    try {
      console.log(`🔍 Fetching theme details for ID: ${themeId}`);
      const response = await themesAPI.getTheme(themeId);
      console.log('✅ Theme details response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching theme details:', {
        themeId: themeId,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error(`Theme with ID ${themeId} not found`);
      } else if (error.response?.status === 500) {
        throw new Error('Server error while loading theme details');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to load theme details');
      }
    }
  },

  createTheme: async (themeData) => {
    try {
      console.log('🆕 Creating new theme:', themeData);
      const response = await themesAPI.createTheme(themeData);
      console.log('✅ Theme created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating theme:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.message || 'Invalid theme data');
      } else if (error.response?.status === 401) {
        throw new Error('Unauthorized to create themes');
      } else if (error.response?.status === 403) {
        throw new Error('Insufficient permissions to create themes');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to create theme');
      }
    }
  },

  updateTheme: async (themeId, themeData) => {
    try {
      console.log(`✏️ Updating theme ${themeId}:`, themeData);
      const response = await themesAPI.updateTheme(themeId, themeData);
      console.log('✅ Theme updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating theme:', {
        themeId: themeId,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error(`Theme with ID ${themeId} not found`);
      } else if (error.response?.status === 403) {
        throw new Error('Insufficient permissions to update themes');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to update theme');
      }
    }
  },

  deleteTheme: async (themeId) => {
    try {
      console.log(`🗑️ Deleting theme ${themeId}`);
      const response = await themesAPI.deleteTheme(themeId);
      console.log('✅ Theme deleted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting theme:', {
        themeId: themeId,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error(`Theme with ID ${themeId} not found`);
      } else if (error.response?.status === 403) {
        throw new Error('Insufficient permissions to delete themes');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to delete theme');
      }
    }
  },

  // Category operations
  getThemeCategories: async (themeId) => {
    try {
      console.log(`📂 Fetching categories for theme ID: ${themeId}`);
      const response = await themesAPI.getThemeCategories(themeId);
      console.log('✅ Categories response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching categories:', {
        themeId: themeId,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error(`Theme with ID ${themeId} not found`);
      } else {
        throw new Error(error.response?.data?.message || 'Failed to load categories');
      }
    }
  },

  createCategory: async (themeId, categoryData) => {
    try {
      console.log(`➕ Creating category for theme ${themeId}:`, categoryData);
      const response = await themesAPI.createCategory(themeId, categoryData);
      console.log('✅ Category created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating category:', {
        themeId: themeId,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error(`Theme with ID ${themeId} not found`);
      } else if (error.response?.status === 400) {
        throw new Error(error.response?.data?.message || 'Invalid category data');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to create category');
      }
    }
  },

  updateCategory: async (themeId, categoryId, categoryData) => {
    try {
      console.log(`✏️ Updating category ${categoryId} for theme ${themeId}:`, categoryData);
      const response = await themesAPI.updateCategory(themeId, categoryId, categoryData);
      console.log('✅ Category updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating category:', {
        themeId: themeId,
        categoryId: categoryId,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error(`Category with ID ${categoryId} not found`);
      } else {
        throw new Error(error.response?.data?.message || 'Failed to update category');
      }
    }
  },

  deleteCategory: async (themeId, categoryId) => {
    try {
      console.log(`🗑️ Deleting category ${categoryId} from theme ${themeId}`);
      const response = await themesAPI.deleteCategory(themeId, categoryId);
      console.log('✅ Category deleted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting category:', {
        themeId: themeId,
        categoryId: categoryId,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error(`Category with ID ${categoryId} not found`);
      } else if (error.response?.status === 400) {
        throw new Error(error.response?.data?.message || 'Cannot delete the last category');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to delete category');
      }
    }
  },

  // Question operations
  getThemeQuestions: async (themeId) => {
    try {
      console.log(`❓ Fetching questions for theme ID: ${themeId}`);
      const response = await themesAPI.getThemeQuestions(themeId);
      console.log('✅ Questions response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching questions:', {
        themeId: themeId,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error(`Theme with ID ${themeId} not found`);
      } else {
        throw new Error(error.response?.data?.message || 'Failed to load questions');
      }
    }
  },

  createQuestion: async (themeId, questionData) => {
    try {
      console.log(`➕ Creating question for theme ${themeId}:`, questionData);
      const response = await themesAPI.createQuestion(themeId, questionData);
      console.log('✅ Question created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating question:', {
        themeId: themeId,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error(`Theme with ID ${themeId} not found`);
      } else if (error.response?.status === 400) {
        throw new Error(error.response?.data?.message || 'Invalid question data');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to create question');
      }
    }
  },

  updateQuestion: async (themeId, questionId, questionData) => {
    try {
      console.log(`✏️ Updating question ${questionId} for theme ${themeId}:`, questionData);
      const response = await themesAPI.updateQuestion(themeId, questionId, questionData);
      console.log('✅ Question updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating question:', {
        themeId: themeId,
        questionId: questionId,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error(`Question with ID ${questionId} not found`);
      } else {
        throw new Error(error.response?.data?.message || 'Failed to update question');
      }
    }
  },

  deleteQuestion: async (themeId, questionId) => {
    try {
      console.log(`🗑️ Deleting question ${questionId} from theme ${themeId}`);
      const response = await themesAPI.deleteQuestion(themeId, questionId);
      console.log('✅ Question deleted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting question:', {
        themeId: themeId,
        questionId: questionId,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        throw new Error(`Question with ID ${questionId} not found`);
      } else {
        throw new Error(error.response?.data?.message || 'Failed to delete question');
      }
    }
  },

  applyTheme: async (applyData) => {
    try {
      console.log('🎯 Applying theme with data:', applyData);
      const response = await themesAPI.applyTheme(applyData);
      console.log('✅ Theme applied successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error applying theme:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to apply themes');
      } else if (error.response?.status === 404) {
        throw new Error('Theme not found');
      } else if (error.response?.status === 500) {
        throw new Error('Server error while applying theme');
      } else {
        throw new Error(error.response?.data?.message || 'Failed to apply theme');
      }
    }
  }
};

export default themesService;